import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, signal, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { take } from 'rxjs';
import { GetAnswerRequest } from '../../../../../../backend/src/core/openai/openai.gate';
import { MarkdownItPipe } from '../../../basic/pipes/markdown-it.pipe';
import { OpenaiModel } from '../../../core/openai/openai';
import { OpenaiService } from '../../../core/openai/openai.service';
import { getCustomerSummaryPrompt } from '../../../core/prompt/resume/get-customer-summary.prompt';
import { Resume } from '../../../core/resume/resume';
import { ResumeService } from '../../../core/resume/resume.service';

@Component({
  selector: 'app-view-resume-details',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,

    NzModalModule,
    NzButtonModule,
    NzInputModule,
    NzPopconfirmModule,
    NzTagModule,
    NzCollapseModule,
    NzTagModule,
    MatTooltip,
    NzPopoverModule,
    NzTabsModule,
    NzDividerModule,
    NzSelectModule,
    NzSkeletonModule,

    MarkdownItPipe,
  ],
  templateUrl: './view-resume-details.component.html',
  styleUrl: './view-resume-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewResumeDetailsComponent {
  @Input({ required: true }) resume!: Resume;

  @Input() isVisible = false;
  @Output() isVisibleChange = new EventEmitter<boolean>();

  isLoading = signal<boolean>(false);

  summary = signal<string>('');

  constructor(
    private openaiService: OpenaiService,
    private resumeService: ResumeService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    for (const propName in changes) {
      if (propName === 'resume') {
        const chng = changes[propName];

        if (chng.previousValue !== chng.currentValue) {
          if (chng.currentValue) {
            if (chng.currentValue.subData?.summary) {
              this.summary.set(chng.currentValue.subData?.summary);
            } else {
              this.getResumeSummary(chng.currentValue);
            }
          }
        }
      }
    }
  }

  onRegenerateClick() {
    this.getResumeSummary(this.resume);
  }

  private getResumeSummary(resume: Resume) {
    this.isLoading.set(true);

    const request: GetAnswerRequest = {
      messageList: [
        {
          role: 'user',
          content: `${getCustomerSummaryPrompt({ resume })}`,
        },
      ],
      config: { openaiModel: OpenaiModel.o1Mini },
    };
    this.openaiService
      .getAnswer$(request)
      .pipe(take(1))
      .subscribe((result) => {
        this.summary.set(result);

        resume.subData.summary = result;

        this.isLoading.set(false);

        this.resumeService.updateResume$(resume).pipe(take(1)).subscribe();
      });
  }

  onIsVisibleChange(isVisible: boolean) {
    this.isVisibleChange.emit(isVisible);
  }

  onCancelClick() {
    this.isVisible = false;
    this.isVisibleChange.emit(this.isVisible);
  }
}
